import { type JSX, useState } from "react";
import styled from "styled-components";
import { useQuery } from "urql";
import Loader from "../../shared/components/Loader";
import StyledModal from "../../shared/components/StyledModal";
import InfoIcon from "../../shared/components/icons/InfoIcon";
import TaskIcon from "../../shared/components/icons/TaskIcon";
import t from "../../shared/translations";
import type { TaskIcon as TaskIconType } from "../../shared/types";
import { useSelectedIds } from "../components/SelectedIds";
import ShowError from "../components/ShowError";
import { type ILearningPathArgs, type ILearningPathData, learningPathQuery } from "./query";

interface ILeaderboard {
    id: string;
    name: string;
    mode: string;
    specifiedTasks: {
        id: string;
    }[];
    specifiedFeedbackTasks: {
        id: string;
    }[];
}

interface IProps {
    leaderboard: ILeaderboard;
}

const InfoIconWrapper = styled.div`
    display: inline-block;
    margin-left: 0.5rem;
    cursor: pointer;
    
    svg {
        width: 32px;
        height: 32px;
        vertical-align: middle;
    }
`;

const TaskList = styled.ul`
    margin: 1rem 0;
    padding-left: 0;
    list-style: none;

    li {
        margin-bottom: 0.5rem;
    }
`;

const TaskItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
        width: 32px;
        height: 32px;
        flex-shrink: 0;
    }
`;

function LeaderboardInfo({ leaderboard }: IProps): JSX.Element {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { pathId } = useSelectedIds();

    const [learningPathResult] = useQuery<ILearningPathData, ILearningPathArgs>({
        query: learningPathQuery,
        variables: { id: pathId || "" },
        pause: leaderboard.mode !== "specific_tasks" || !pathId,
    });

    const openInfo = () => {
        setIsModalOpen(true);
    };

    const closeInfo = () => {
        setIsModalOpen(false);
    };

    const getModeDescription = () => {
        if (leaderboard.mode === "specific_tasks") {
            return t("player.performance.info-leaderboard-mode-specific-tasks-description");
        }
        return t("player.performance.info-leaderboard-mode-all-tasks-description");
    };

    const getOrderedTasks = () => {
        if (leaderboard.mode !== "specific_tasks" || !learningPathResult.data) {
            return [];
        }

        const specifiedTaskIds = new Set(leaderboard.specifiedTasks.map((t) => t.id));
        const specifiedFeedbackTaskIds = new Set(leaderboard.specifiedFeedbackTasks.map((t) => t.id));
        const orderedTasks: Array<{ id: string; icon: TaskIconType; shortTitle: string }> = [];

        for (const item of learningPathResult.data.learningPath.learningPathItems) {
            if (item.type === "event" || item.type === "submitted-task-only") {
                continue;
            }

            for (const task of item.tasks) {
                if (specifiedTaskIds.has(task.id)) {
                    orderedTasks.push(task);
                }
            }

            if (item.feedbackTask && specifiedFeedbackTaskIds.has(item.feedbackTask.id)) {
                orderedTasks.push(item.feedbackTask);
            }
        }

        return orderedTasks;
    };

    const orderedTasks = getOrderedTasks();

    return (
        <>
            <InfoIconWrapper onClick={openInfo}>
                <InfoIcon />
            </InfoIconWrapper>
            <StyledModal
                isOpen={isModalOpen}
                onRequestClose={closeInfo}
                contentLabel={`Leaderboard Info: ${leaderboard.name}`}
            >
                <h4>{leaderboard.name}</h4>
                <p>{getModeDescription()}</p>
                {leaderboard.mode === "specific_tasks" && (
                    <>
                        {learningPathResult.fetching && <Loader />}
                        {learningPathResult.error && <ShowError />}
                        {orderedTasks.length > 0 && (
                            <>
                                <h5>{t("player.performance.leaderboard-tasks-heading")}</h5>
                                <TaskList>
                                    {orderedTasks.map((task) => (
                                        <li key={task.id}>
                                            <TaskItem>
                                                <TaskIcon icon={task.icon} />
                                                <span>{task.shortTitle}</span>
                                            </TaskItem>
                                        </li>
                                    ))}
                                </TaskList>
                            </>
                        )}
                    </>
                )}
            </StyledModal>
        </>
    );
}

export default LeaderboardInfo;
